import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { COMPANY_GROUP_TAG, PROFILE_TAG } from "constants/tagCategory";
import { CHURNED, DISSOLVED, MOVE_TO_SALES, REMOVED } from "constants/TagType";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import ThreeDots from "static/images/ThreeDots.svg";
import { HandoverDetailsModal } from "components/serviceTeam/HandoverDetailsModal";
import { useGetTagsForCategoryQuery } from "store/apis/billing";
import {
  useGetLatestHandoverDetailQuery,
  useUpdateGroupDataMutation,
  useUpdateUserDataMutation,
} from "store/apis/salesCrm";
import { Tag } from "types/Models/FCA";
import { BackendError } from "types/utils/error";
import { ConfirmationModal } from "./ConfirmationModal";
import { MoveToSalesModal } from "./MoveToSalesModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useAppDispatch } from "hooks/useAppDispatch";
import { openChurnDetailsModal } from "store/slices/churnDetailsModal";

const EMPTY_TAGS: Tag[] = [];

type CrmTableDropdownProps = {
  actionOn: "group" | "profile";
  showMoveToSalesOption?: boolean;
  // Sales CRM: static "Move to ops" action that starts the handover.
  showMoveToOpsOption?: boolean;
  // Ops CRM: same modal, but labeled "Add"/"View Handover Details" depending
  // on whether one was already submitted.
  showHandoverDetailsOption?: boolean;
  uuid: string;
  // The backend sends `null` (not just omitting the field) when a group/profile has no
  // tags yet, so a default parameter alone won't catch it -- normalize below instead.
  tags?: Tag[] | null;
};

export const CrmTableDropdown = ({
  actionOn,
  showMoveToSalesOption = false,
  showMoveToOpsOption = false,
  showHandoverDetailsOption = false,
  uuid,
  tags,
}: CrmTableDropdownProps) => {
  const isForProfile = actionOn === "profile";
  const isAlreadyChurned = (tags ?? EMPTY_TAGS).some(
    ({ title }) => title === CHURNED
  );
  const { alertToast, successToast } = useToast();
  const { data: tagsData = [] } = useGetTagsForCategoryQuery({
    tagType: isForProfile ? PROFILE_TAG : COMPANY_GROUP_TAG,
  });
  const { isAdmin } = useRoleBasedView();
  const { data: latestHandoverDetail } = useGetLatestHandoverDetailQuery(
    { groupId: uuid },
    { skip: !showHandoverDetailsOption || !isAdmin }
  );
  const handoverDetailsLabel = latestHandoverDetail
    ? "View Handover Details"
    : "Add Handover Details";
  const [updateGroup, { isLoading: isUpdatingGroup }] =
    useUpdateGroupDataMutation();
  const [updateUser, { isLoading: isUpdatingUser }] =
    useUpdateUserDataMutation();
  const removeModal = useModal();
  const churnModal = useModal();
  const dissolveModal = useModal();
  const salesModal = useModal();
  const handoverDetailsModal = useModal();
  const dispatch = useAppDispatch();

  const openGroupChurnDetails = () => dispatch(openChurnDetailsModal(uuid));

  // Groups require churn details before the CHURNED tag can be applied (see the
  // comment on CHURNED in constants/TagType.ts) -- profiles don't have that form yet,
  // so they still go through the plain confirm-and-tag flow below.
  const handleChurnedSelect = isForProfile
    ? churnModal.open
    : openGroupChurnDetails;

  const getGroupActionPayload = (tagTitle: string) => {
    const tag_id = tagsData.find(({ title }) => title === tagTitle)?.uuid;
    switch (tagTitle) {
      case REMOVED: {
        return { tag_id };
      }
      case CHURNED: {
        return { tag_id };
      }
      case DISSOLVED: {
        return { tag_id };
      }
      case MOVE_TO_SALES: {
        return {
          crm_type: "SALES_CRM",
        };
      }
      default: {
        return {};
      }
    }
  };

  const getProfileActionPayload = (tagTitle: string) => {
    const tag_id = tagsData.find(({ title }) => title === tagTitle)?.uuid || "";
    switch (tagTitle) {
      case REMOVED: {
        return { tag_id };
      }
      case CHURNED: {
        return { tag_id };
      }
      case DISSOLVED: {
        return { tag_id };
      }
      default: {
        return { tag_id };
      }
    }
  };

  const updateGroupData = async (
    tagTitle:
      | typeof CHURNED
      | typeof DISSOLVED
      | typeof MOVE_TO_SALES
      | typeof REMOVED,
    closeModal: () => void
  ) => {
    try {
      if (isForProfile) {
        const payload = getProfileActionPayload(tagTitle);
        await updateUser({
          payload: payload,
          profileId: uuid,
        }).unwrap();
        successToast({ message: "User updated successfully!" });
        closeModal();
        return;
      }

      const payload = getGroupActionPayload(tagTitle);
      await updateGroup({
        payLoad: payload,
        groupId: uuid,
      }).unwrap();
      successToast({ message: "Group updated successfully!" });
      closeModal();
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} data-testid="crm-table-dropdown">
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <img src={ThreeDots} alt="Show more options" />
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content sideOffset={6} align="end">
            {showMoveToSalesOption && (
              <Dropdown.Item onSelect={salesModal.open} key="moveToSales">
                Move to sales
              </Dropdown.Item>
            )}
            {showMoveToOpsOption && (
              <Dropdown.Item
                onSelect={handoverDetailsModal.open}
                key="moveToOps"
              >
                Move to ops
              </Dropdown.Item>
            )}
            {showHandoverDetailsOption && (
              <Dropdown.Item
                onSelect={handoverDetailsModal.open}
                key="handoverDetails"
              >
                {handoverDetailsLabel}
              </Dropdown.Item>
            )}
            <Dropdown.Item onSelect={removeModal.open} key="remove">
              Remove
            </Dropdown.Item>
            <Dropdown.Item
              onSelect={handleChurnedSelect}
              disabled={isAlreadyChurned}
              key="churned"
            >
              Churned
            </Dropdown.Item>
            <Dropdown.Item onSelect={dissolveModal.open} key="dissolved">
              Dissolved
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      {/* remove modal */}
      <ConfirmationModal
        show={removeModal.isOpen}
        close={removeModal.close}
        action={"Remove"}
        actionOn={actionOn}
        handleClick={() => updateGroupData(REMOVED, removeModal.close)}
        isLoading={isUpdatingGroup || isUpdatingUser}
      />
      {/* churn modal (profiles only -- groups use ChurnDetailsModalHost in GlobalModals) */}
      <ConfirmationModal
        show={churnModal.isOpen}
        close={churnModal.close}
        action={"Churn"}
        actionOn={actionOn}
        handleClick={() => updateGroupData(CHURNED, churnModal.close)}
        isLoading={isUpdatingGroup || isUpdatingUser}
      />
      {/* dissolve modal */}
      <ConfirmationModal
        show={dissolveModal.isOpen}
        close={dissolveModal.close}
        action={"Dissolve"}
        actionOn={actionOn}
        handleClick={() => updateGroupData(DISSOLVED, dissolveModal.close)}
        isLoading={isUpdatingGroup || isUpdatingUser}
      />
      {/* move to sales */}
      <MoveToSalesModal
        show={salesModal.isOpen}
        close={salesModal.close}
        handleMoveToSales={() =>
          updateGroupData(MOVE_TO_SALES, salesModal.close)
        }
        isLoading={isUpdatingGroup}
      />
      <HandoverDetailsModal
        isOpen={handoverDetailsModal.isOpen}
        close={handoverDetailsModal.close}
        groupId={uuid}
        onSubmitted={handoverDetailsModal.close}
      />
    </div>
  );
};
